import { Box, chakra, Flex, Image, Text } from "@chakra-ui/react";
import React from "react";
import { LuInfo, LuMessageCircleHeart, LuX } from "react-icons/lu";
import type { Companion } from "src/models/companion";
import { ACCENT_GRADIENT, withAlpha } from "src/theme";
import { tap } from "src/utils/haptic";
import { ThemeBadge } from "./ThemeBadge";

/** How far (px) a card must be dragged to count as a swipe. */
const SWIPE_AT = 110;
const FLY_MS = 300;
/** Cards drawn: the top one and two peeking behind it. */
const STACK = 3;

type Fly = "left" | "right" | null;

function DeckCard({ companion }: { companion: Companion }) {
  return (
    <Box
      position="absolute"
      inset={0}
      borderRadius="30px"
      overflow="hidden"
      bg={withAlpha(companion.color, 0.35)}
      boxShadow={`0 20px 50px ${withAlpha(companion.color, 0.3)}, 0 2px 0 rgba(255,255,255,0.06) inset`}
      userSelect="none"
    >
      {companion.avatarURL && (
        <Image
          src={companion.avatarURL}
          alt={companion.name}
          boxSize="100%"
          objectFit="cover"
          draggable={false}
          pointerEvents="none"
        />
      )}
      <Box
        position="absolute"
        inset={0}
        bg="linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 45%, rgba(14,10,10,0.92) 100%)"
      />
      <Box position="absolute" top={4} left={4}>
        <ThemeBadge theme={companion.theme} />
      </Box>
      <Box position="absolute" left={0} right={0} bottom={0} p={5}>
        <Text
          fontSize="34px"
          fontWeight="bold"
          letterSpacing="-0.02em"
          lineHeight="1"
        >
          {companion.name}
        </Text>
        <Flex
          display="inline-flex"
          align="center"
          gap={1.5}
          mt={3}
          px={3}
          py={1}
          borderRadius="full"
          bg={withAlpha(companion.color, 0.85)}
          fontSize="xs"
          fontWeight="bold"
          color="white"
          textShadow="0 1px 2px rgba(0,0,0,0.3)"
        >
          {companion.tagline}
        </Flex>
        <Text fontSize="sm" color="rgba(255,255,255,0.82)" mt={2} lineClamp={2}>
          {companion.bio}
        </Text>
      </Box>
    </Box>
  );
}

function Stamp({
  label,
  color,
  side,
  opacity,
}: {
  label: string;
  color: string;
  side: "left" | "right";
  opacity: number;
}) {
  return (
    <Box
      position="absolute"
      top="28px"
      {...(side === "left" ? { left: "22px" } : { right: "22px" })}
      transform={`rotate(${side === "left" ? -14 : 14}deg) scale(${0.8 + opacity * 0.2})`}
      opacity={opacity}
      px={3}
      py={1}
      borderWidth="3px"
      borderColor={color}
      borderRadius="12px"
      color={color}
      fontSize="28px"
      fontWeight="black"
      letterSpacing="0.08em"
      bg="rgba(0,0,0,0.25)"
      pointerEvents="none"
      zIndex={3}
    >
      {label}
    </Box>
  );
}

/**
 * One companion at a time, as a card stack. Drag (or the buttons, or the
 * arrow keys) right to chat, left to skip; tap the card for their profile.
 * Skipping loops through the list. The position survives a visit to a chat.
 */
export function SwipeDeck({
  companions,
  onChat,
  onInfo,
  storageKey,
}: {
  companions: readonly Companion[];
  onChat: (c: Companion) => void;
  onInfo: (c: Companion) => void;
  /** sessionStorage key for the deck position. */
  storageKey: string;
}) {
  const n = companions.length;
  const [pos, setPos] = React.useState(() => {
    try {
      return Number(sessionStorage.getItem(storageKey)) || 0;
    } catch {
      return 0;
    }
  });
  const [dx, setDx] = React.useState(0);
  const [dy, setDy] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const [fly, setFly] = React.useState<Fly>(null);
  const start = React.useRef<{ x: number; y: number; moved: boolean } | null>(
    null,
  );
  const pastThreshold = React.useRef(false);

  const top = n > 0 ? companions[((pos % n) + n) % n] : null;

  const swipe = React.useCallback(
    (dir: "left" | "right") => {
      if (!top || fly) return;
      tap();
      setFly(dir);
      window.setTimeout(() => {
        // Saved before a right swipe navigates away and unmounts the deck.
        const next = pos + 1;
        try {
          sessionStorage.setItem(storageKey, String(next));
        } catch {
          // Private mode: the deck just starts at the top next time.
        }
        setPos(next);
        setFly(null);
        setDx(0);
        setDy(0);
        if (dir === "right") onChat(top);
      }, FLY_MS);
    },
    [top, fly, onChat, pos, storageKey],
  );

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        /input|textarea/i.test(e.target.tagName)
      )
        return;
      if (e.key === "ArrowRight") swipe("right");
      if (e.key === "ArrowLeft") swipe("left");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [swipe]);

  if (!top) return null;

  function onPointerDown(e: React.PointerEvent) {
    if (fly) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    start.current = { x: e.clientX, y: e.clientY, moved: false };
    pastThreshold.current = false;
    setDragging(true);
  }
  function onPointerMove(e: React.PointerEvent) {
    const s = start.current;
    if (!s) return;
    const x = e.clientX - s.x;
    const y = e.clientY - s.y;
    if (Math.abs(x) > 6 || Math.abs(y) > 6) s.moved = true;
    const past = Math.abs(x) > SWIPE_AT;
    if (past !== pastThreshold.current) {
      pastThreshold.current = past;
      if (past) tap();
    }
    setDx(x);
    setDy(y);
  }
  function onPointerUp() {
    const s = start.current;
    start.current = null;
    setDragging(false);
    if (!s) return;
    if (dx > SWIPE_AT) return swipe("right");
    if (dx < -SWIPE_AT) return swipe("left");
    if (!s.moved && top) onInfo(top);
    setDx(0);
    setDy(0);
  }

  const progress = Math.min(Math.abs(dx) / SWIPE_AT, 1);
  const topTransform = fly
    ? `translate(${fly === "right" ? 140 : -140}%, ${dy}px) rotate(${fly === "right" ? 24 : -24}deg)`
    : `translate(${dx}px, ${dy * 0.35}px) rotate(${dx / 16}deg)`;

  const stack = Array.from(
    { length: Math.min(STACK, n) },
    (_, i) => companions[(((pos + i) % n) + n) % n],
  );

  return (
    <Flex
      direction="column"
      align="center"
      gap={4}
      width="100%"
      flex="1"
      minH={0}
    >
      {/* Fills the space it is given (the parent is a flex column). */}
      <Box position="relative" width="100%" flex="1" minH="280px" maxH="600px">
        {stack
          .map((c, i) => {
            if (i === 0) {
              return (
                <Box
                  key={`${c.id}-${pos}`}
                  position="absolute"
                  inset={0}
                  zIndex={STACK}
                  touchAction="none"
                  cursor={dragging ? "grabbing" : "grab"}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  style={{
                    transform: topTransform,
                    transition: dragging
                      ? "none"
                      : `transform ${FLY_MS}ms cubic-bezier(0.2, 0.8, 0.3, 1)`,
                  }}
                  role="group"
                  aria-label={`${c.name}. Swipe right to chat, left to skip, tap for profile.`}
                >
                  <DeckCard companion={c} />
                  <Stamp
                    label="CHAT"
                    color="#5be38a"
                    side="left"
                    opacity={fly === "right" ? 1 : dx > 0 ? progress : 0}
                  />
                  <Stamp
                    label="SKIP"
                    color="#ff6b6b"
                    side="right"
                    opacity={fly === "left" ? 1 : dx < 0 ? progress : 0}
                  />
                </Box>
              );
            }
            // Cards behind move up as the top one leaves.
            const lift = fly ? 1 : progress;
            const depth = i - (i === 1 ? lift : lift * 0.5);
            return (
              <Box
                key={`${c.id}-${pos + i}`}
                position="absolute"
                inset={0}
                zIndex={STACK - i}
                pointerEvents="none"
                style={{
                  transform: `translateY(${depth * 16}px) scale(${1 - depth * 0.05})`,
                  opacity: 1 - depth * 0.25,
                  transition: dragging
                    ? "none"
                    : `transform ${FLY_MS}ms ease, opacity ${FLY_MS}ms ease`,
                }}
              >
                <DeckCard companion={c} />
              </Box>
            );
          })
          .reverse()}
      </Box>

      <Flex align="center" gap={5} flexShrink={0}>
        <RoundButton
          label="Skip"
          size="58px"
          onClick={() => swipe("left")}
          color="#ff6b6b"
          bg="elevated"
        >
          <LuX size={28} strokeWidth={2.6} />
        </RoundButton>
        <RoundButton
          label={`About ${top.name}`}
          size="46px"
          onClick={() => onInfo(top)}
          color="white"
          bg="elevated"
        >
          <LuInfo size={20} />
        </RoundButton>
        <RoundButton
          label={`Chat with ${top.name}`}
          size="68px"
          onClick={() => swipe("right")}
          color="white"
          bg={ACCENT_GRADIENT}
          glow
        >
          <LuMessageCircleHeart size={30} />
        </RoundButton>
      </Flex>
    </Flex>
  );
}

function RoundButton({
  label,
  size,
  onClick,
  color,
  bg,
  glow,
  children,
}: React.PropsWithChildren<{
  label: string;
  size: string;
  onClick: () => void;
  color: string;
  bg: string;
  glow?: boolean;
}>) {
  return (
    <chakra.button
      type="button"
      aria-label={label}
      onClick={onClick}
      boxSize={size}
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color={color}
      background={bg}
      boxShadow={
        glow ? "0 10px 30px rgba(255,98,87,0.4)" : "0 6px 18px rgba(0,0,0,0.4)"
      }
      borderWidth={glow ? 0 : "1px"}
      borderColor="line"
      transition="transform 0.15s"
      _hover={{ transform: "scale(1.05)" }}
      _active={{ transform: "scale(0.9)" }}
    >
      {children}
    </chakra.button>
  );
}
